import {
  Building2,
  ClipboardCheck,
  LayoutDashboard,
  Link2,
  List,
  ListTodo,
  Map,
  Network,
  Pencil,
  Puzzle,
  RefreshCw,
  ServerOff,
  Settings,
  UserCog,
  Waypoints
} from 'lucide-react';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import type { NavMainItem } from './NavMain';

// Installed apps whose nav entry and breadcrumb carry the Beta badge.
export const BETA_APP_IDS = new Set([
  'system-pair-analyzer',
  'compliance-check',
  'omniscient',
  'document-capture'
]);

/** The Processes group; its children also drive the process tab bar. */
export const processesNavItem: NavMainItem = {
  title: 'common.Sidebar.Processes',
  url: '/processes',
  icon: Waypoints,
  requiredPermission: { subject: 'Processes' },
  items: [
    {
      title: 'common.Sidebar.AllProcesses',
      url: '/processes/all-processes',
      icon: List
    },
    // TODO: Review AI Code for feature: Process Map
    {
      title: 'common.Sidebar.ProcessLandscape',
      url: '/processes/process-map',
      icon: Map,
      requiredPermission: { subject: 'ProcessMap' }
    },
    {
      title: 'common.Sidebar.ProcessChains',
      url: '/processes/process-chains',
      icon: Link2,
      requiredPermission: { subject: 'ProcessMap' }
    },
    {
      title: 'common.Sidebar.ProcessEditor',
      url: '/processes/editor',
      icon: Pencil,
      requiredPermission: { subject: 'ProcessEditor' }
    }
  ]
};

export const mainNavigation: NavMainItem[] = [
  {
    title: 'common.Sidebar.Dashboard',
    url: '/',
    icon: LayoutDashboard,
    items: [
      {
        title: 'common.Sidebar.MyOverview',
        url: '/',
        icon: LayoutDashboard,
        requiredPermission: { subject: 'Dashboard' }
      },
      {
        title: 'common.Sidebar.RecordingDashboard',
        url: '/recording-dashboard',
        icon: ClipboardCheck,
        requiredPermission: { subject: 'RecordingDashboard' }
      }
    ]
  },
  {
    title: 'common.Sidebar.MyTasks',
    url: '/my-tasks',
    icon: ListTodo,
    requiredPermission: { subject: 'MyTasks' }
  },
  processesNavItem,
  {
    title: 'common.Sidebar.Systems',
    url: '/systems',
    icon: ENTITY_ICONS.system,
    items: [
      {
        title: 'common.Sidebar.MySystems',
        url: '/systems',
        icon: ENTITY_ICONS.system,
        requiredPermission: { subject: 'MySystems' }
      },
      {
        title: 'common.Sidebar.SystemLandscape',
        url: '/systems/landscape',
        icon: Network,
        requiredPermission: { subject: 'SystemLandscape' }
      },
      {
        title: 'common.Sidebar.ShadowSystems',
        url: '/systems/shadow-systems',
        icon: ServerOff,
        requiredPermission: { subject: 'ShadowSystem' }
      }
    ]
  },
  {
    title: 'common.Sidebar.Organization',
    icon: ENTITY_ICONS.department,
    keepGroup: true,
    items: [
      {
        title: 'common.Sidebar.MyOrgFunctions',
        url: '/org-functions',
        icon: ENTITY_ICONS.orgFunction,
        requiredPermission: { subject: 'MyOrgFunctions' }
      },
      {
        title: 'common.Sidebar.MyDepartment',
        url: '/department',
        icon: ENTITY_ICONS.department,
        requiredPermission: { subject: 'MyDepartment' }
      },
      {
        title: 'common.Sidebar.OrgChart',
        url: '/organigram',
        icon: Network,
        requiredPermission: { subject: 'OrgChart' }
      }
    ]
  },
  {
    title: 'common.Sidebar.OrgCatalogs',
    url: '/organization',
    icon: Building2,
    requiredPermission: { subject: 'OrgOverview' },
    items: [
      {
        title: 'common.Sidebar.AllDepartments',
        url: '/organization/departments',
        icon: ENTITY_ICONS.department
      },
      {
        title: 'common.Sidebar.AllFunctions',
        url: '/organization/functions',
        icon: ENTITY_ICONS.role
      },
      {
        title: 'common.Sidebar.AllTeams',
        url: '/organization/teams',
        icon: ENTITY_ICONS.team
      },
      {
        title: 'common.Sidebar.AllMembers',
        url: '/organization/org-members',
        icon: ENTITY_ICONS.orgMember
      },
      {
        title: 'common.Sidebar.AllSoftwareSystems',
        url: '/organization/systems',
        icon: ENTITY_ICONS.system
      },
      {
        title: 'common.Sidebar.AllExternalContacts',
        url: '/organization/external-contacts',
        icon: ENTITY_ICONS.externalContact
      },
      {
        title: 'common.Sidebar.AllArtifacts',
        url: '/organization/artifacts',
        icon: ENTITY_ICONS.artifact
      }
    ]
  },
  {
    title: 'common.Sidebar.OrgSettings',
    url: '/organization-settings',
    icon: Settings,
    keepGroup: true,
    items: [
      {
        title: 'common.Sidebar.Settings',
        url: '/organization-settings/settings',
        icon: Settings,
        requiredPermission: { subject: 'OrgSettings' }
      },
      {
        title: 'common.Sidebar.UserManagement',
        url: '/organization-settings/user-management',
        icon: UserCog,
        requiredPermission: { subject: 'OrgSettings' }
      },
      {
        title: 'common.Sidebar.ManageApps',
        url: '/organization-settings/apps',
        icon: Puzzle,
        requiredPermission: { subject: 'OrgSettings' }
      },
      {
        title: 'common.Sidebar.DataSync',
        url: '/organization-settings/data-sync',
        icon: RefreshCw,
        beta: true,
        requiredPermission: { subject: 'DataSync', action: 'update' }
      }
    ]
  }
];

// Breadcrumb segments that carry the Beta badge. App routes end with the appId
// (app-internal navigation lives in search params), so beta app ids double as
// segments.
export const BETA_SEGMENTS = new Set([
  ...BETA_APP_IDS,
  ...mainNavigation
    .flatMap((item) => [item, ...(item.items ?? [])])
    .flatMap(({ beta, url }) => (beta && url ? [url.slice(url.lastIndexOf('/') + 1)] : []))
]);
